import React, { useState, useMemo } from 'react';
import { Calendar, dateFnsLocalizer, View } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { parseISO } from 'date-fns';
import { useEffect } from 'react';
import { isToday } from 'date-fns/isToday';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { RecurringEventActionModal } from './RecurringEventActionModal';
import ViewEventModal from './ViewEventModal';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import { API_BASE_URL } from '../../config/api';

const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Create drag-and-drop calendar
const DnDCalendar = withDragAndDrop(Calendar);

type SlotMinutes = 15 | 30;
const SLOT_MINUTES_STORAGE_KEY = 'hp_slot_minutes';
const SLOT_MINUTES_EVENT = 'hp_slot_minutes_changed';

export type AvailabilityType = 'AVAILABLE' | 'SURGERY_BLOCK' | 'UNAVAILABLE' | 'EMERGENCY_ONLY';

export interface WorkScheduleEvent {
  id: string;
  vet_user_id: string;
  practice_id: string;
  start_at: string;
  end_at: string;
  availability_type: AvailabilityType;
  title?: string;
  notes?: string;
  is_active: boolean;
  is_recurring?: boolean;
  recurring_template_id?: string;
}

export interface TeamMember {
  id: string;
  full_name: string;
  email: string;
  role: string;
}

interface WorkScheduleCalendarProps {
  scheduleEvents: WorkScheduleEvent[];
  onSelectEvent: (eventId: string) => void;
  onSelectSlot?: (slotInfo: { start: Date; end: Date }) => void;
  onRescheduleEvent?: (eventId: string, start: Date, end: Date) => void;
  onRefresh?: () => Promise<void>;
  currentDate?: Date;
  onNavigate?: (date: Date) => void;
  currentView?: View;
  onViewChange?: (view: View) => void;
  selectedVetIds?: Set<string>;
  teamMembers?: TeamMember[];
}

const WorkScheduleCalendar: React.FC<WorkScheduleCalendarProps> = ({
  scheduleEvents,
  onSelectEvent,
  onSelectSlot,
  onRescheduleEvent,
  onRefresh,
  currentDate,
  onNavigate,
  currentView,
  onViewChange,
  selectedVetIds,
  teamMembers = [],
}) => {
  const [view, setView] = useState<View>(currentView || 'week');
  const [date, setDate] = useState(currentDate || new Date());
  const [slotMinutes, setSlotMinutes] = useState<SlotMinutes>(() => {
    const saved = localStorage.getItem(SLOT_MINUTES_STORAGE_KEY);
    return saved === '30' ? 30 : 15;
  });

  // State for view event modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedEventForView, setSelectedEventForView] = useState<WorkScheduleEvent | null>(null);

  // State for recurring event action modal
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [recurringModalAction, setRecurringModalAction] = useState<'edit' | 'delete' | 'move'>('edit');
  const [pendingRecurringAction, setPendingRecurringAction] = useState<{
    event: any;
    newStartTime?: Date;
    newEndTime?: Date;
  } | null>(null);

  // State for confirm delete modal (non-recurring events)
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Use controlled props if provided, otherwise use local state
  const effectiveView = currentView || view;
  const effectiveDate = currentDate || date;

  // Keep slot minutes in sync across views (swim lanes + calendars)
  useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail as { slotMinutes?: number } | undefined;
        const next = detail?.slotMinutes === 30 ? 30 : 15;
        setSlotMinutes(next);
      } catch {
        // ignore
      }
    };
    window.addEventListener(SLOT_MINUTES_EVENT, handler as EventListener);
    return () => window.removeEventListener(SLOT_MINUTES_EVENT, handler as EventListener);
  }, []);

  // Get doctor color based on their index in the team members array (matching avatar colors)
  const getDoctorColor = (vetUserId: string): string => {
    const colors = [
      '#3b82f6', // blue-500
      '#10b981', // emerald-500
      '#a855f7', // purple-500
      '#f97316', // orange-500
      '#ec4899', // pink-500
      '#6366f1', // indigo-500
      '#14b8a6', // teal-500
      '#f43f5e', // rose-500
    ];

    const index = teamMembers.findIndex(member => member.id === vetUserId);
    if (index === -1) return colors[0]; // Default to blue if not found

    return colors[index % colors.length];
  };

  // Get opacity based on availability type
  const getOpacityForAvailability = (availabilityType: AvailabilityType): number => {
    switch (availabilityType) {
      case 'AVAILABLE':
        return 0.95; // Full opacity
      case 'SURGERY_BLOCK':
        return 0.85; // Slightly less
      case 'UNAVAILABLE':
        return 0.5; // More transparent
      case 'EMERGENCY_ONLY':
        return 0.75; // Medium transparency
      default:
        return 0.95;
    }
  };

  const handleViewChange = (newView: View) => {
    if (onViewChange) {
      onViewChange(newView);
    } else {
      setView(newView);
    }
  };

  const handleNavigate = (newDate: Date) => {
    if (onNavigate) {
      onNavigate(newDate);
    } else {
      setDate(newDate);
    }
  };

  // Convert schedule events to calendar events
  const events = useMemo(() => {
    // Filter events based on selected vet IDs
    const filteredEvents = selectedVetIds && selectedVetIds.size > 0
      ? scheduleEvents.filter(event => selectedVetIds.has(event.vet_user_id))
      : scheduleEvents;

    return filteredEvents.map((event) => {
      // Parse UTC date and convert to local time
      const start = parseISO(event.start_at);
      const end = parseISO(event.end_at);

      // Get title: use custom title if provided, otherwise fall back to availability type
      let title = '';
      if (event.title) {
        title = event.title;
      } else {
        switch (event.availability_type) {
          case 'AVAILABLE':
            title = 'Available';
            break;
          case 'SURGERY_BLOCK':
            title = 'Surgery Block';
            break;
          case 'UNAVAILABLE':
            title = 'Unavailable';
            break;
          case 'EMERGENCY_ONLY':
            title = 'Emergency Only';
            break;
        }
      }

      if (event.notes) {
        title += ` - ${event.notes}`;
      }

      return {
        id: event.id,
        title,
        start,
        end,
        resource: event,
        availabilityType: event.availability_type,
      };
    });
  }, [scheduleEvents, selectedVetIds]);

  // Event style getter for color coding by doctor
  const eventStyleGetter = (event: any) => {
    const vetUserId = event.resource?.vet_user_id;
    const availabilityType = event.availabilityType;

    // Get doctor's color (matching avatar)
    const backgroundColor = vetUserId ? getDoctorColor(vetUserId) : '#3b82f6';

    // Use opacity to differentiate availability types
    const opacity = getOpacityForAvailability(availabilityType);

    return {
      style: {
        backgroundColor,
        borderRadius: '5px',
        opacity,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '13px',
        padding: '2px 5px',
      },
    };
  };

  const handleSelectEvent = (event: any) => {
    // On click, always show the view modal first
    setSelectedEventForView(event.resource);
    setShowViewModal(true);
  };

  const handleEditFromView = () => {
    // Close view modal and open edit modal directly (even for recurring events)
    setShowViewModal(false);

    if (!selectedEventForView) return;

    // Always open edit modal directly - the edit modal will handle showing recurring options on save
    onSelectEvent(selectedEventForView.id);
  };

  const handleDeleteFromView = () => {
    // Close view modal
    setShowViewModal(false);

    if (!selectedEventForView) return;

    // Check if this is a recurring event
    if (selectedEventForView.is_recurring || selectedEventForView.recurring_template_id) {
      setPendingRecurringAction({ event: selectedEventForView });
      setRecurringModalAction('delete');
      setShowRecurringModal(true);
    } else {
      // For non-recurring events, show custom confirmation modal
      setShowConfirmDelete(true);
    }
  };

  const performNonRecurringDelete = async () => {
    if (!selectedEventForView) return;

    setIsDeleting(true);

    try {
      const jwt_token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/scheduling-unix/vet-availability/${selectedEventForView.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${jwt_token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete event');
      }

      // Close modal and refresh
      setShowConfirmDelete(false);
      setSelectedEventForView(null);

      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('❌ [WorkScheduleCalendar] Error deleting event:', error);
      // Error will be shown in the modal via isDeleting state
      alert('Failed to delete event. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSelectSlot = (slotInfo: any) => {
    if (onSelectSlot) {
      onSelectSlot({
        start: slotInfo.start,
        end: slotInfo.end,
      });
    }
  };

  const handleEventDrop = ({ event, start, end }: any) => {
    // Check if this is a recurring event
    if (event.resource?.is_recurring || event.resource?.recurring_template_id) {
      setPendingRecurringAction({
        event: event.resource,
        newStartTime: start,
        newEndTime: end
      });
      setRecurringModalAction('move');
      setShowRecurringModal(true);
    } else if (onRescheduleEvent) {
      onRescheduleEvent(event.id, start, end);
    }
  };

  const handleEventResize = ({ event, start, end }: any) => {
    // Check if this is a recurring event
    if (event.resource?.is_recurring || event.resource?.recurring_template_id) {
      setPendingRecurringAction({
        event: event.resource,
        newStartTime: start,
        newEndTime: end
      });
      setRecurringModalAction('move');
      setShowRecurringModal(true);
    } else if (onRescheduleEvent) {
      onRescheduleEvent(event.id, start, end);
    }
  };

  // Handle recurring event modal confirmation
  const handleRecurringModalConfirm = async (scope: 'single' | 'following' | 'all') => {
    if (!pendingRecurringAction) return;

    const { event, newStartTime, newEndTime } = pendingRecurringAction;
    const jwt_token = localStorage.getItem('token');
    const templateId = event.recurring_template_id;

    if (!templateId) {
      console.error('No template ID found for recurring event');
      return;
    }

    try {
      if (recurringModalAction === 'move' && newStartTime && newEndTime && onRescheduleEvent) {
        if (scope === 'single') {
          // Create an exception for this date with new times
          const instanceDate = format(parseISO(event.start_at), 'yyyy-MM-dd');
          await fetch(
            `${API_BASE_URL}/api/v1/scheduling/recurring-availability/${templateId}/modify-single?instance_date=${instanceDate}`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${jwt_token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                new_start_at: newStartTime.toISOString(),
                new_end_at: newEndTime.toISOString(),
                new_availability_type: event.availability_type,
              })
            }
          );
        } else if (scope === 'following') {
          // Split the template at this date
          const splitDate = format(parseISO(event.start_at), 'yyyy-MM-dd');
          await fetch(
            `${API_BASE_URL}/api/v1/scheduling/recurring-availability/${templateId}/split?split_date=${splitDate}&new_start_time=${format(newStartTime, 'HH:mm')}&new_end_time=${format(newEndTime, 'HH:mm')}`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${jwt_token}`,
                'Content-Type': 'application/json'
              }
            }
          );
        } else if (scope === 'all') {
          // Update the entire template
          await fetch(
            `${API_BASE_URL}/api/v1/scheduling/recurring-availability/${templateId}`,
            {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${jwt_token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                start_time: format(newStartTime, 'HH:mm:ss'),
                end_time: format(newEndTime, 'HH:mm:ss'),
                availability_type: event.availability_type
              })
            }
          );
        }
        // Refresh the schedule data without losing context
        if (onRefresh) {
          await onRefresh();
        }
      } else if (recurringModalAction === 'edit') {
        // For edit, open the edit modal
        onSelectEvent(event.id);
      } else if (recurringModalAction === 'delete') {
        if (scope === 'single') {
          // Delete this specific instance by adding EXDATE
          const instanceDate = format(parseISO(event.start_at), 'yyyy-MM-dd');
          await fetch(
            `${API_BASE_URL}/api/v1/scheduling/recurring-availability/${templateId}/delete-single?instance_date=${instanceDate}`,
            {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${jwt_token}`,
                'Content-Type': 'application/json'
              }
            }
          );
        } else if (scope === 'following') {
          // End the template before this date
          const endDate = format(parseISO(event.start_at), 'yyyy-MM-dd');
          await fetch(
            `${API_BASE_URL}/api/v1/scheduling/recurring-availability/${templateId}`,
            {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${jwt_token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                effective_until: endDate
              })
            }
          );
        } else if (scope === 'all') {
          // Delete the entire template
          await fetch(
            `${API_BASE_URL}/api/v1/scheduling/recurring-availability/${templateId}`,
            {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${jwt_token}`
              }
            }
          );
        }
        // Refresh the schedule data without losing context
        if (onRefresh) {
          await onRefresh();
        }
      }
    } catch (error) {
      console.error('Error handling recurring event action:', error);
    } finally {
      // Clean up and close modal
      setShowRecurringModal(false);
      setPendingRecurringAction(null);
    }
  };

  const CustomDayHeader = ({ date }: { date: Date }) => {
    const today = isToday(date);
    const day = format(date, 'EEE').toUpperCase();
    const dayNum = format(date, 'd');
    return (
      <div className="flex flex-col items-center justify-center leading-none py-1">
        <div className="text-[11px] tracking-wider text-gray-500 dark:text-gray-400">{day}</div>
        <div
          className={[
            'mt-1 w-10 h-10 flex items-center justify-center rounded-full text-[26px] font-medium',
            today ? 'bg-blue-600 text-white' : 'text-gray-900 dark:text-dark-text-primary',
          ].join(' ')}
        >
          {dayNum}
        </div>
      </div>
    );
  };

  const CustomMonthHeader = ({ date }: { date: Date }) => {
    const day = format(date, 'EEE').toUpperCase();
    return (
      <div className="flex items-center justify-center py-1">
        <div className="text-[11px] tracking-wider text-gray-500 dark:text-gray-400">{day}</div>
      </div>
    );
  };

  // Custom event component to show title in week/day view
  const CustomEvent = ({ event }: any) => {
    return (
      <div className="flex flex-col h-full overflow-hidden">
        <div className="font-semibold text-xs truncate">{event.title}</div>
      </div>
    );
  };

  return (
    <div className="h-full bg-white">
      <DnDCalendar
        className={effectiveView === 'month' ? undefined : (slotMinutes === 15 ? 'hp-rbc-slots-15' : 'hp-rbc-slots-30')}
        localizer={localizer}
        events={events}
        startAccessor={(event: any) => event.start}
        endAccessor={(event: any) => event.end}
        style={{ height: 'calc(100vh - 200px)', minHeight: '600px' }}
        view={effectiveView}
        onView={handleViewChange}
        date={effectiveDate}
        onNavigate={handleNavigate}
        onSelectEvent={handleSelectEvent}
        onSelectSlot={handleSelectSlot}
        onEventDrop={handleEventDrop}
        onEventResize={handleEventResize}
        selectable
        resizable
        eventPropGetter={eventStyleGetter}
        popup
        step={slotMinutes}
        timeslots={slotMinutes === 15 ? 4 : 2}
        defaultView="week"
        views={['month', 'week', 'day']}
        toolbar={true}
        components={{
          event: CustomEvent,
          header: effectiveView === 'month' ? CustomMonthHeader : CustomDayHeader,
        }}
      />

      {/* Legend */}
      <div className="flex items-center gap-4 p-4 border-t border-gray-200 text-xs">
        <span className="font-medium text-gray-700">Availability Type:</span>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-green-600"></div>
          <span>Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-blue-600"></div>
          <span>Surgery Block</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-red-600"></div>
          <span>Unavailable</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-orange-600"></div>
          <span>Emergency Only</span>
        </div>
      </div>

      {/* View Event Modal */}
      <ViewEventModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        event={selectedEventForView}
        onEdit={handleEditFromView}
        onDelete={handleDeleteFromView}
      />

      {/* Recurring Event Action Modal */}
      <RecurringEventActionModal
        isOpen={showRecurringModal}
        onClose={() => setShowRecurringModal(false)}
        onConfirm={handleRecurringModalConfirm}
        actionType={recurringModalAction}
      />

      {/* Confirm Delete Modal (for non-recurring events) */}
      <ConfirmDeleteModal
        isOpen={showConfirmDelete}
        onClose={() => {
          setShowConfirmDelete(false);
          setIsDeleting(false);
        }}
        onConfirm={performNonRecurringDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default WorkScheduleCalendar;

